import React from 'react';
import { Box, Avatar, Badge, Tabs, Tab, Typography, Tooltip } from '@mui/material';
import { Star } from '@mui/icons-material';
import { formatDateOnly } from '../../services/adminService';

/**
 * Derive display initials.
 *
 * Falls back to the username's first character rather than rendering an empty
 * circle, because an account can exist before a name is captured.
 */
const initialsFor = (detail) => {
  const first = (detail.first_name || '').trim();
  const last = (detail.last_name || '').trim();

  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }
  return (detail.username || '?').charAt(0).toUpperCase();
};

// subscription_expiration arrives as a bare date (e.g. "2026-09-01"), which
// formatDateOnly anchors to local midnight — unanchored, `new Date(value)`
// would parse it as UTC midnight and render a day early in any
// negative-UTC-offset timezone.
const formatExpiration = (value) => {
  const formatted = formatDateOnly(value);
  return formatted ? `Subscriber · expires ${formatted}` : 'Subscriber';
};

function UserIdentityRail({ detail, activeTab, onTabChange, isMobile }) {
  const name = [detail.first_name, detail.last_name].filter(Boolean).join(' ');

  const avatar = (
    <Avatar sx={{ width: 64, height: 64, bgcolor: 'secondary.main', fontSize: '1.5rem', mx: 'auto' }}>
      {initialsFor(detail)}
    </Avatar>
  );

  return (
    <Box
      sx={{
        width: isMobile ? '100%' : 220,
        flexShrink: 0,
        borderRight: isMobile ? 0 : 1,
        borderBottom: isMobile ? 1 : 0,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ p: 2, textAlign: 'center' }}>
        {detail.active_subscription ? (
          <Tooltip title={formatExpiration(detail.subscription_expiration)}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              aria-label="subscriber"
              badgeContent={<Star sx={{ fontSize: 14, color: 'common.white' }} />}
              sx={{ '& .MuiBadge-badge': { bgcolor: 'primary.main', height: 22, minWidth: 22, borderRadius: '50%' } }}
            >
              {avatar}
            </Badge>
          </Tooltip>
        ) : (
          avatar
        )}

        <Typography variant="subtitle1" sx={{ mt: 1.5, fontWeight: 600 }}>
          {name || 'Unnamed user'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
          {detail.username}
        </Typography>
      </Box>

      <Tabs
        orientation={isMobile ? 'horizontal' : 'vertical'}
        variant={isMobile ? 'scrollable' : 'standard'}
        value={activeTab}
        onChange={(event, value) => onTabChange(value)}
        aria-label="User detail sections"
        sx={{ borderRight: 0 }}
      >
        <Tab label="Overview" id="user-detail-tab-0" aria-controls="user-detail-tabpanel-0" />
        <Tab label="Permissions" id="user-detail-tab-1" aria-controls="user-detail-tabpanel-1" />
      </Tabs>
    </Box>
  );
}

export default UserIdentityRail;
